/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';

import {
  LaunchModeFrequencies,
  LaunchModeParameters,
  Skill,
  SkillStatus,
  SkillType
} from '../../../../ts/skill';
import { Snapshot } from '../../../../ts/snapshot';

export const CONVERSATION_TEMPLATE_ID = '72dbb4e4-4dd9-4019-bd46-3af4f9dea20f';
export const DISCOVERY_TEMPLATE_ID = '7671fa04-bc36-11e8-a355-529269fb1459';
export const ORCHESTRATOR_SKILL_TEMPLATE_ID =
  '5f109c46-9851-4721-835a-4550848b5f31';

// This is the latest algo version as of 2023/12/08, yyyy-mm-dd
export const ALGO_VERSION_UPDATE_DATE = '2023-04-15';

/**
 * @deprecated Use `SkillType` instead.
 */
export const SKILL_TYPE = {
  ACTION: 'action',
  ACTION_OR_DIALOG: 'actionOrDialog',
  DIALOG: 'dialog',
  [CONVERSATION_TEMPLATE_ID]: 'dialog',
  SEARCH: 'search',
  [DISCOVERY_TEMPLATE_ID]: 'search',
  [ORCHESTRATOR_SKILL_TEMPLATE_ID]: 'orchestrator',
  ORCHESTRATOR: 'orchestrator'
};

/**
 * @deprecated Use `SkillReferenceType` instead.
 */
export const SKILL_REFERENCE = {
  [SKILL_TYPE.ACTION]: 'actions skill',
  CONVERSATION: 'main skill',
  [SKILL_TYPE.DIALOG]: 'main skill',
  [CONVERSATION_TEMPLATE_ID]: 'main skill',
  SEARCH: 'search skill',
  [SKILL_TYPE.SEARCH]: 'search skill',
  [DISCOVERY_TEMPLATE_ID]: 'search skill',
  OTHER: 'custom skill',
  [ORCHESTRATOR_SKILL_TEMPLATE_ID]: 'orchestrator skill'
};

export const BUTTON_ID = {
  [SKILL_TYPE.DIALOG]: 'Dialog',
  [SKILL_TYPE.SEARCH]: 'Search'
};

export const VALIDATORS = {
  name: {
    length: 64
  },
  description: {
    length: 128
  }
};

export const LAUNCH_MODE_LEARNING_DEFAULT: LaunchModeParameters = {
  single_answer_clarification: LaunchModeFrequencies.MORE_OFTEN,
  disambiguation_clarification: LaunchModeFrequencies.MORE_OFTEN,
  use_connect_to_support: LaunchModeFrequencies.MORE_OFTEN,
  step_retry_threshold: 2
};

export const LAUNCH_MODE_CONFIDENT_DEFAULT: LaunchModeParameters = {
  single_answer_clarification: LaunchModeFrequencies.SOMETIMES,
  disambiguation_clarification: LaunchModeFrequencies.SOMETIMES,
  use_connect_to_support: LaunchModeFrequencies.SOMETIMES,
  step_retry_threshold: 4
};

export const sleep = async (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms));

export const isProcessing = (skill: Partial<Skill | Snapshot>) =>
  !skill?.status || skill?.status === SkillStatus.PROCESSING;

/**
 * @deprecated Use `SkillStatus` instead.
 */
export const SKILL_STATUS = {
  AVAILABLE: 'Available',
  FAILED: 'Failed',
  PROCESSING: 'Processing'
};

export const getSkillTypeFromReference = ref => {
  if (ref === SKILL_REFERENCE[SKILL_TYPE.ACTION]) {
    return SKILL_TYPE.ACTION;
  }
  if (ref === SKILL_REFERENCE[SKILL_TYPE.DIALOG]) {
    return SKILL_TYPE.DIALOG;
  }
  return SKILL_TYPE.SEARCH;
};

export const getSkillType = (skill: Skill): SkillType => {
  if (skill.type === SkillType.DIALOG) {
    if (skill?.dialog_settings?.actions) {
      return SkillType.ACTION;
    }
    return SkillType.DIALOG;
  } else if (skill.type === SkillType.ACTION) {
    return SkillType.ACTION;
  }

  return SkillType.SEARCH;
};

export const isSearchSkill = (skill: Skill) =>
  getSkillType(skill) === SKILL_TYPE.SEARCH;

/* eslint-disable valid-jsdoc */
/**
 * This function is for the compatibility of v2 skill and v1 workspace
 * We are using workspace everywhere in the dialog skill
 * Before we totally move those codes,
 * we still need the workspace object even if we are using v2 APIs
 * @param {{
 *   skill_id: string;
 *   name: string;
 *   description: string;
 *   language: string;
 *   created: string;
 *   updated: string;
 *   snapshot_references: {snapshot: string, description: string}[];
 *   workspace_reference: {
 *     system_settings: object;
 *     learning_opt_out: boolean;
 *     status: string;
 *     metadata: object
 *   }
 * }} skill a verbose skill object that contains full workspace info
 * @returns {{
 *   workspace_id: string;
 *   skill_id: string;
 *   name: string;
 *   description: string;
 *   language: string;
 *   created: string;
 *   updated: string;
 *   learning_opt_out: boolean;
 *   system_settings: object;
 *   status: string;
 *   metadata: object
 * }} the corresponding workspace object
 */
export const toWorkspace = (skill: Skill) => ({
  ..._.pick(skill, [
    'workspace_id',
    'skill_id',
    'name',
    'description',
    'language',
    'created',
    'updated'
  ]),
  ...skill.workspace_reference
});

/**
 * This function turn a workspace to a skill structure
 * @param {{
 *   workspace_id: string;
 *   skill_id: string;
 *   name: string;
 *   description: string;
 *   language: string;
 *   created: string;
 *   updated: string;
 *   learning_opt_out: boolean;
 *   system_settings: object;
 *   status: string;
 *   metadata: object;
 *   dialog_nodes: [];
 *   intents: [];
 *   entities: [];
 *   counterexamples: [];
 *   system_settings: {};
 *   imports: object;
 *   webhooks: [];
 * }} workspace a verbose workspace object that contains full skill info
 * @returns {{
 *   skill_id: string;
 *   name: string;
 *   description: string;
 *   language: string;
 *   created: string;
 *   updated: string;
 *   snapshot_references: {snapshot: string, description: string}[];
 *   workspace: {
 *     system_settings: {};
 *     learning_opt_out: boolean;
 *     status: string;
 *     metadata: object,
 *     intents: [],
 *     entities: [],
 *     dialog_nodes: [],
 *     webhooks: []
 *   }
 * }} the corresponding exported skill object
 */
export const toSkill = workspace => ({
  ..._.pick(workspace, [
    'skill_id',
    'name',
    'description',
    'language',
    'created',
    'updated'
  ]),
  workspace: _.pick(workspace, [
    'actions',
    'system_settings',
    'learning_opt_out',
    'metadata',
    'intents',
    'entities',
    'dialog_nodes',
    'webhooks'
  ])
});

export const addMatchingWorkspaceToSkill = ({ skill, skills, workspaces }) => {
  if (
    skill &&
    workspaces &&
    Array.isArray(Object.keys(workspaces)) &&
    skills &&
    skills.list &&
    skills.list.length &&
    skill &&
    skill.config &&
    skill.config.workspace_id &&
    workspaces[skill.config.workspace_id]
  ) {
    return {
      ...skill,
      workspace: workspaces[skill.config.workspace_id]
    };
  }
  return skill;
};
